/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useMemo,
  useState
} from 'react';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import get from 'lodash/get';
import has from 'lodash/has';
import {translate} from '@ion/core/src/locale/locale';
import {BulkSettingFieldValues, GenericObject, SCIPendingSettings} from '@ion/core/src/types/entities';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import RadioButton from '@ion/dls/src/components/radio/RadioButton';
import Switch from '@ion/dls/src/components/switch/Switch';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {BulkSCIStyleProps} from './BulkSCISettings';
import {
  pendingValueCheck,
  reportingIntervalInitialVal
} from './SCISettingsUtils';

export interface SCIReportsTabProps extends BulkSCIStyleProps {
  reportTabFields: GenericObject[];
  classes: {
    [key: string]: string;
  };
  handleReportAndLogs: Function;
  noUpdate: boolean;
  sciPendingSettingData: SCIPendingSettings;
  getIsActive: Function;
  initialReportAndLog: boolean;
  bulkSCI?: boolean;
}

export interface GetOptions {
  value: any;
  label: any;
};

export type GetOptionsResult = GetOptions[];
/**
 * View or Edit Reports related information of an SCI device
 */
export const SCIReportsTab = ({
  reportTabFields,
  classes,
  handleReportAndLogs,
  noUpdate,
  sciPendingSettingData,
  getIsActive,
  initialReportAndLog,
  bulkSCI = false,
  isBulkAction = false
}: SCIReportsTabProps) => {
  const [reportsActiveTab, setReportsActiveTab] = useState<number>(0);

  const reportAndLogOptions = useMemo((): GetOptionsResult => {
    const entries = translate('admin-devices:view.sciSettings.reportOptions', {
      returnObjects : true
    });
    return Object.entries(entries).map(([value, label]) => {
      return {value, label};
    });
  }, []);

  const accuracyOptions = useMemo((): GetOptionsResult => {
    const option = [];
    for (let i = 0; i <= 6; i++) {
      option.push({value: i, label: i.toString()});
    }
    return option;
  }, []);

  function handleChange(event: ChangeEvent<{}>, newValue: any) {
    setReportsActiveTab(newValue);
  };

  const isReportingInterval = useCallback((field): boolean => {
    return (field.label === translate('admin-devices:view.sciSettings.reportingInterval'));
  }, []);

  const isAccuracy = useCallback((field): boolean => {
    return (field.label === translate('admin-devices:view.sciSettings.accuracy'));
  }, []);

  const isReportMovement = useCallback((field): boolean => {
    return (field.label === translate('admin-devices:view.sciSettings.reportMovementImmediately'));
  }, []);

  const fields = useMemo(() => (
    `${BulkSettingFieldValues.on} ${isBulkAction ? BulkSettingFieldValues.bulkDefault : BulkSettingFieldValues.default} ${BulkSettingFieldValues.off}`
  ), []);

  const getRadioField = (field, value) => {
    return (
      <>
        <RadioButton label
          checked={value}
          value={BulkSettingFieldValues.on}
          onChange={function(e) { handleReportAndLogs(e, field, reportsActiveTab); }}/>
        <RadioButton label
          checked={value === null}
          value={BulkSettingFieldValues.default}
          onChange={function(e) { handleReportAndLogs(e, field, reportsActiveTab); }}/>
        <RadioButton label
          checked={value === false}
          value={BulkSettingFieldValues.off}
          onChange={function(e) { handleReportAndLogs(e, field, reportsActiveTab); }}/>
      </>
    );
  };

  const reportAndLogsUI = (activeFieldsData, activeTab) => {
    return (
      <div>
        {activeFieldsData?.length ? activeFieldsData.map((field, {label}) => {
          return (
            <div>
              {Object.entries(field).map(([key, value]) => {
                if (key !== 'label') {
                  const dropDownValue = initialReportAndLog ? sciPendingSettingData[key] : value;
                  return (<div>
                    {isReportingInterval(field) && (
                      <div className={has(sciPendingSettingData, key)
                        ? classes?.reportPaper
                        : isBulkAction ? classes.bulkIdReportPaper : classes?.idReportPaper}>
                        <FormItem customWidth={9} disableMinWidth>
                          <div className={classes.actionsTile}>
                            <BodyTypography className={classes.overridesReportTitle}>
                              {field.label}
                            </BodyTypography>
                            <div className={classes?.reportsFields}>
                              <Dropdown displaySelectedLabel
                                options={reportAndLogOptions}
                                value={reportingIntervalInitialVal(key, dropDownValue, bulkSCI)}
                                onChange={function(e) {
                                  handleReportAndLogs(e?.target?.value, field, activeTab);
                                }}/>
                            </div>
                          </div>
                        </FormItem>
                        {!isEmpty(sciPendingSettingData) && has(sciPendingSettingData, key) && (
                          <span className={classes.reportsPendingStatus}>
                            {!isUndefined(get(sciPendingSettingData, key)) && (
                              <span>
                                ({translate('admin-devices:view.sciSettings.pending')}
                                {pendingValueCheck(get(sciPendingSettingData, key))})
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    {isAccuracy(field) && (
                      <div className={has(sciPendingSettingData, key)
                        ? classes?.reportPaper
                        : isBulkAction ? classes.bulkIdReportPaper : classes?.idReportPaper}>
                        <FormItem customWidth={9} disableMinWidth>
                          <div className={classes.actionsTile}>
                            <BodyTypography className={classes.overridesReportTitle}>
                              {field.label}
                            </BodyTypography>
                            <div className={classes?.reportsFields}>
                              <Dropdown displaySelectedLabel
                                options={accuracyOptions}
                                value={value?.toString()}
                                onChange={function(e) {
                                  handleReportAndLogs(e, field, activeTab);
                                }}/>
                            </div>
                          </div>
                        </FormItem>
                        {!isEmpty(sciPendingSettingData) && has(sciPendingSettingData, key) && (
                          <span className={classes.reportsPendingStatus}>
                            {!isUndefined(get(sciPendingSettingData, key)) && (
                              <span>
                                ({translate('admin-devices:view.sciSettings.pending')}
                                {pendingValueCheck(get(sciPendingSettingData, key))})
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                    {(isReportMovement(field)) && (
                      <div className={has(sciPendingSettingData, key)
                        ? classes?.reportPaper
                        : isBulkAction ? classes.bulkIdReportPaper : classes?.idReportPaper}>
                        {bulkSCI && (
                          <div className={isBulkAction ? classes.bulkSensorRadioLabel : classes.sensorRadioLabel}>
                            {fields}
                          </div>)}
                        <FormItem customWidth={9} disableMinWidth>
                          <div className={classes.actionsTile}>
                            <BodyTypography className={classes.overridesReportTitle}>
                              {field.label}
                            </BodyTypography>
                            {!bulkSCI
                              ? <Switch disabled={false}
                                name={field.label}
                                onChange={function(e) {
                                  handleReportAndLogs(!value, field, activeTab);
                                }}
                                checked={getIsActive(value)}/>
                              : getRadioField(field, value)
                            }
                          </div>
                        </FormItem>
                        {!isEmpty(sciPendingSettingData) && has(sciPendingSettingData, key) && (
                          <span className={classes.pendingStatus}>
                            {!isUndefined(get(sciPendingSettingData, key)) && (
                              <span>
                                ({translate('admin-devices:view.sciSettings.pending')}
                                {pendingValueCheck(get(sciPendingSettingData, key))})
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  );
                }
              })}
            </div>
          );
        }) : <div className={classes.noDataStyle}>{translate('admin-devices:view.sciSettings.noRowsMessage')}</div>}
      </div>
    );
  };
  return (
    <div>
      <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
        <Tabs value={reportsActiveTab} onChange={handleChange}>
          <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.stationary')} />
          <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.moving')} />
          <Tab className={classes.tabStyle} label={translate('admin-devices:view.sciSettings.ignition')} />
        </Tabs>
      </Box>
      {reportAndLogsUI(reportTabFields[reportsActiveTab], reportsActiveTab)}
    </div>
  );
};

export default SCIReportsTab;
